export interface ElectronAPI {

	// #region Public functions

	/**
	 * Set the window always on top
	 * @param alwaysOnTop True to set the window always on top, false to remove it
	 */
	setAlwaysOnTop: (alwaysOnTop: boolean) => void

	/**
	 * Set the zoom level of the window, 10-200%
	 * @param zoom The zoom level to set, in percent
	 */
	setZoom: (zoom: number) => void

	/**
	 * Set the name of the app
	 * @param name The name of the app
	 */
	setAppName: (name: string) => void

	/**
	 * Set the window fullscreen or not
	 * @param fullscreen True to set the window fullscreen, false to remove it
	 */
	setFullscreen: (fullscreen: boolean) => void

	/**
	 * Set the autolaunch of the app
	 * @param enabled True to enable autolaunch, false to disable it
	 * @returns True if the operation was successful, false if it failed
	 */
	setAutoLaunch: (enabled: boolean) => Promise<boolean>

	/**
	 * Set global shortcuts
	 * @param shortcuts Global shortcuts to register - key: shortcut, value: action
	 * @returns True if the operation was successful, false if it failed
	 */
	setGlobalShortcuts: (shortcuts: Record<string, string>) => Promise<boolean>

	/**
	 * Set the window bounds
	 * @param x X coordinate of the window
	 * @param y Y coordinate of the window
	 * @param width Width of the window
	 * @param height Height of the window
	 */
	setWindowBounds: (x: number, y: number, width: number, height: number) => void

	/**
	 * Ping the Electron app to check if it is alive and responding - also returns the version of the app
	 * @returns Version of the Electron app
	 * @throws Errors if electron app does not respond
	 */
	ping: () => Promise<string>

	/**
	 * Open app or URL via shell
	 * @param url to open - HTTP, MAILTO etc or path+app to launch
	 */
	openExternal: (url: string) => void

	/**
	 * Trigger the send keys integration
	 * @param data Data to send to the integration
	 * @returns True if the operation was successful, false if it failed
	 */
	integrationSendKeys: (data: IntegrationSendKeysData) => Promise<boolean>

	/**
	 * Trigger the browser integration
	 * @param data Data to send to the integration
	 * @returns True if the operation was successful, false if it failed
	 */
	integrationBrowser: (data: IntegrationBrowserData) => Promise<boolean>

	/**
	 * Trigger the run command integration
	 * @param command Command to run
	 * @returns True if the operation was successful, false if it failed
	 */
	integrationRun: (command: string) => Promise<boolean>

	/**
	 * Trigger the clipboard integration
	 * @param text Text to copy to the clipboard
	 * @returns True if the operation was successful, false if it failed
	 */
	integrationClipboard: (text: string) => Promise<boolean>

	/**
	 * Hide the main window
	 * @param popup Optional parameter to indicate if invoked due to call popup
	 */
	hide: (popup?: boolean) => void

	/**
	 * Show the main window
	 * @param popup Optional parameter to indicate if invoked due to call popup
	 */
	show: (popup?: boolean) => void

	/**
	 * Download a file from the internet
	 * @param url URL to download
	 * @param filename Filename to save as
	 * @param showFolderPicker True to show the folder picker, false to save in the default location
	 */
	download: (url: string, filename: string, showFolderPicker: boolean) => Promise<boolean>

	/**
	 * Terminate the app
	 */
	terminate: () => void

	/**
	 * Show or hide the dev tools
	 * @param show True to show the dev tools, false to hide them
	 */
	devTools: (show: boolean) => void

	/**
	 * Send a request to the local zerotier API
	 * @param url URL in zerotier API to invoke
	 * @param method HTTP method to use
	 * @param body Body object to send with the request if method is POST
	 * @returns Response from zerotier API
	 */
	zerotier: (url: string, method: 'GET' | 'POST' | 'DELETE', body?: string) => Promise<ZerotierResponse>

	/**
	 * Reply to invoke when main process ask if we are still alive
	 * @param failed True if the app is in a failed state
	 */
	aliveReply: (failed: boolean) => void

	/**
	 * Show a system notification (Windows only)
	 * @param title Title of the notification
	 * @param body Body of the notification
	 * @param icon Icon to show in the notification
	 * @param tag Tag to identify the notification
	 */
	notificationShow: (title: string, body: string | undefined, icon: string | undefined, tag: string) => Promise<NotificationShowResponse>

	/**
	 * Hide a previously shown system notification (Windows only)
	 * @param tag Tag to identify the notification
	 */
	notificationClose: (tag: string) => void

	/**
	 * Set the state of the beacon scanner
	 * @param enabled True to enable the beacon scanner, false to disable it
	 * @param uuid iBeacon UUID to filter the beacons on
	 */
	setBeaconScanner: (enabled: boolean, uuid?: string) => void

	// #endregion

	// #region Events (main to renderer)

	/**
	 * Main process requests that we call the provided phone number
	 * @param callback Callback to be called when the app requests that we make a call
	 */
	onCall: (callback: (number: string) => void) => void

	/**
	 * Main process informs that the windows has changed to/from fullscreen
	 * @param callback Callback to be called when the window changed to/from fullscreen
	 */
	onFullscreen: (callback: (fullscreen: boolean) => void) => void

	/**
	 * Main process informs that the window visibility has changed
	 * @param callback Callback to be called when the window visibility changes
	 */
	onVisibility: (callback: (visible: boolean) => void) => void

	/**
	 * Main process asks if we are still alive, reply with aliveReply.
	 * @param callback Callback to be called when the app is alive
	 */
	onAlive: (callback: () => void) => void

	/**
	 * Main process informs that a global shortcut key has been pressed
	 * @param callback Callback to be called when a global shortcut is pressed
	 */
	onGlobalShortcut: (callback: (command: 'ANSWER' | 'HANGUP' | 'CALL' | 'SEARCH', number?: string) => void) => void

	/**
	 * Main process informs that an error has occurred and an error message is to be displayed
	 * @param callback Callback to be called when an error occurs and a message is to be displayed
	 */
	onError: (callback: (header: string, message: string) => void) => void

	/**
	 * Main process informs that a log message is to be recorded
	 * @param callback Callback to be called when a log message is to be recorded
	 */
	onLog: (callback: (severity: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG', message: string) => void) => void
	// #endregion

	/**
	 * Main process informs that the user has locked or unlock the desktop
	 */
	onLock: (callback: (locked: boolean) => void) => void

	/**
	 * Main process informs that the window bounds have changed
	 * @param callback Callback to be called when the window bounds change
	 */
	onWindowBounds: (callback: (x: number, y: number, width: number, height: number) => void) => void

	/**
	 * Main process inform that a beacon event has occurred
	 * @param callback Callback to be called when a beacon event occurs
	 */
	onBeacon: (callback: (event: BeaconEvent, beacon?: Beacon) => void) => void

	// #region Yealink headset integration (desktop shell v29+)

	/**
	 * Start the Yealink DECT USB headset integration - loads the native SDK in the main process and starts scanning for devices.
	 * Subscribe with onYealink BEFORE calling this so no deviceAttached events are missed.
	 * Idempotent - calling while already running just returns the current state.
	 * @param options Optional appId/softphoneName overrides - defaults to the TelecomX App ID and the current app name
	 * @returns Result envelope with the current state (running, sdkVersion, attached devices) on success
	 */
	yealinkStart: (options?: YealinkStartOptions) => Promise<YealinkResult<YealinkState>>

	/**
	 * Stop the Yealink integration - releases devices and callbacks. Can be started again with yealinkStart.
	 */
	yealinkStop: () => Promise<YealinkResult>

	/**
	 * Get the current state of the Yealink integration - running flag, SDK version and all attached devices with their supported features
	 */
	yealinkGetState: () => Promise<YealinkState>

	/**
	 * Get name-to-value maps for all Yealink SDK enums (CallAction, BusylightStatus, DeviceFeature, ...) - fetch once and cache
	 */
	yealinkGetEnums: () => Promise<YealinkEnums>

	/**
	 * Check if a device supports a feature before using optional functionality
	 * @param deviceId Device to address
	 * @param feature DeviceFeature enum value (see yealinkGetEnums)
	 */
	yealinkIsFeatureSupported: (deviceId: number, feature: number) => Promise<YealinkResult<boolean>>

	/** Go off-hook / on-hook (single-call convenience - prefer the multi-call API) */
	yealinkSetOffHook: (deviceId: number, offHook: boolean) => Promise<YealinkResult>

	/** Hold / resume the active call (single-call convenience - prefer the multi-call API) */
	yealinkSetHold: (deviceId: number, hold: boolean) => Promise<YealinkResult>

	/** Mute / unmute the microphone (single-call convenience - prefer the multi-call API) */
	yealinkSetMute: (deviceId: number, mute: boolean) => Promise<YealinkResult>

	/** Start / stop the ringer (single-call convenience - prefer the multi-call API) */
	yealinkSetRing: (deviceId: number, ring: boolean) => Promise<YealinkResult>

	/** Drive the online/offline presence indicator on the base display */
	yealinkSetOnline: (deviceId: number, online: boolean) => Promise<YealinkResult>

	/**
	 * Put the base into dial-entry state - the Send button then produces a 'callAction' event with action CallOut and the typed number
	 * @param deviceId Device to address
	 * @param cid Caller-assigned call id - use the same cid across the lifetime of one logical call
	 */
	yealinkEnterDial: (deviceId: number, cid: number) => Promise<YealinkResult>

	/** Play pre-answer ringback for an outgoing call */
	yealinkRingBack: (deviceId: number, cid: number) => Promise<YealinkResult>

	/** Report an inbound call - the base rings and shows the contact's name and number */
	yealinkIncomingCall: (deviceId: number, cid: number, contact: YealinkContact) => Promise<YealinkResult>

	/** Transition an in-progress outgoing call to active */
	yealinkOutgoingCall: (deviceId: number, cid: number, contact: YealinkContact) => Promise<YealinkResult>

	/** Accept an incoming call - also the required ack when the user presses Accept on the device */
	yealinkAnswerCall: (deviceId: number, cid: number) => Promise<YealinkResult>

	/** Place an active call on hold - contact refreshes the display label while on hold */
	yealinkHoldCall: (deviceId: number, cid: number, contact: YealinkContact) => Promise<YealinkResult>

	/** Resume a held call */
	yealinkResumeCall: (deviceId: number, cid: number) => Promise<YealinkResult>

	/** Hang up a call - also the required ack when the user presses Terminate/Reject on the device */
	yealinkTerminateCall: (deviceId: number, cid: number) => Promise<YealinkResult>

	/** Mute the microphone for a call - also the required ack when the user presses Mute on the device */
	yealinkMuteCall: (deviceId: number, cid: number) => Promise<YealinkResult>

	/** Unmute the microphone for a call - also the required ack when the user presses Unmute on the device */
	yealinkUnmuteCall: (deviceId: number, cid: number) => Promise<YealinkResult>

	/** Refresh the display for an in-progress call (e.g. on blind-transfer or CNAM update) */
	yealinkUpdateContact: (deviceId: number, cid: number, contact: YealinkContact) => Promise<YealinkResult>

	/**
	 * Push a contact list the user can browse on the device - replaces the current list, pass [] to clear.
	 * The device holds at most ~2500 entries and overflow is SILENT - keep pushes comfortably below that.
	 */
	yealinkSetPhonebook: (deviceId: number, contacts: YealinkContact[]) => Promise<YealinkResult>

	/** Set the contact shown during the current active call */
	yealinkSetCurrentTalkContact: (deviceId: number, contact: YealinkContact) => Promise<YealinkResult>

	/**
	 * Set the busylight indicator LED on the base - requires the BusyLight feature
	 * @param status BusylightStatus enum value (see yealinkGetEnums)
	 */
	yealinkSetBusylight: (deviceId: number, status: number) => Promise<YealinkResult>

	/** Read the current busylight LED state - result is a BusylightStatus enum value */
	yealinkGetBusylight: (deviceId: number) => Promise<YealinkResult<number>>

	/** Read the battery state of the headset */
	yealinkGetBatteryStatus: (deviceId: number) => Promise<YealinkResult<YealinkBatteryStatus>>

	/** Play a built-in ringtone - valid type values are firmware-specific */
	yealinkPlayRingtone: (deviceId: number, type: number) => Promise<YealinkResult>

	/** Upload a custom ringtone - the file must be in Yealink's ringtone format and the path is resolved on the local machine */
	yealinkUploadRingtone: (deviceId: number, filePath: string) => Promise<YealinkResult>

	/** Reboot the device - expect a deviceRemoved followed by a deviceAttached event */
	yealinkReboot: (deviceId: number) => Promise<YealinkResult>

	/** Factory reset the device - DESTRUCTIVE, prompt the user first */
	yealinkFactoryReset: (deviceId: number) => Promise<YealinkResult>

	/**
	 * Push wall-clock date/time to the device
	 * @param dateTime ISO string or epoch milliseconds - omit to use the current time
	 */
	yealinkSetDateTime: (deviceId: number, dateTime?: string | number) => Promise<YealinkResult>

	/** Read the Yealink-internal identifier (distinct from the USB serial number) */
	yealinkGetEsn: (deviceId: number) => Promise<YealinkResult<string>>

	/** Read the device firmware version, e.g. '103.3.0.10' */
	yealinkGetFirmwareVersion: (deviceId: number) => Promise<YealinkResult<string>>

	/**
	 * Main process informs that a Yealink headset event has occurred (device attach/remove, call actions, dialpad input, battery, busylight, ...).
	 * The event is a discriminated union on the 'type' field - see YealinkEvent.
	 * Subscribe before calling yealinkStart so no events are missed.
	 */
	onYealink: (callback: (event: YealinkEvent) => void) => void

	// #endregion

}

// #region interfaces

/**
 * Mock Window interface for exposing Electron API
 */
export interface WindowElectronAPI {
	/** The exposed Electron API */
	electronAPI: ElectronAPI
}

export interface IntegrationSendKeysData {
	/** Name of process to give forcus to */
	focusProcess: string
	/** Shall the process be maximized */
	maximizeProcess: boolean
	/** Use AutoIT instead of system to sending keystrokes */
	useAutoit: boolean
	/** String to key out */
	keys: string
}

export interface IntegrationBrowserData {
	/** hostname of website to be active on */
	website: string
	/** True if target website is a single-page-application */
	spa: boolean
	/** List of actions to perform */
	actions: IntegrationBrowserAction[]
}

export interface IntegrationBrowserAction {
	action: IntegrationBrowserActionType
	value?: number | string // sleep=number, target=string, insert=string, js=string
}

export enum IntegrationBrowserActionType {
	/** Sleep for x milliseconds */
	sleep = 'sleep',
	/** Select a DOM target for subsequent actions */
	target = 'target',
	/** Click on the target */
	click = 'click',
	/** Focus the target */
	focus = 'focus',
	/** Clear the target of type input/textarea */
	clear = 'clear',
	/** Insert text into the target */
	insert = 'insert',
	/** Execute the provided javascript */
	js = 'js',
	/** Scroll to top of the page */
	scrolltop = 'scrolltop'
}

// #endregion

export type IntegrationGetResponse = IntegrationGetItem[]

export interface IntegrationGetItem {
	/** Unique identifier */
	messageId: number

	/** Command to execute */
	command: IntegrationGetCommand

	/** Phone number to call (command CALL) */
	number?: string

	/** Sucess (command PING, FULLSCREEN) */
	success?: boolean

	/** Version of wrapper app (command PING) */
	version?: string

	/** Header for error message to display (command ERROR) */
	header?: string

	/** Error message to display (command ERROR) or log message to store (command LOGGER) */
	message?: string

	/** Severity of log message (command LOGGER) */
	severity?: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG'

	/** When command is FULLSCREEN, this shows if fullscreen was enabled or disabled */
	fullscreen?: boolean
}

export enum IntegrationGetCommand {
	/** Call number i number */
	CALL = 'CALL',
	/** Display error message in header and message */
	ERROR = 'ERROR',
	/** Log event in message and severity */
	LOGGER = 'LOGGER',
	/** Answer ringing call */
	ANSWER = 'ANSWER',
	/** Hangup call */
	HANGUP = 'HANGUP',
	/** Focus search box */
	SEARCH = 'SEARCH',
	/** Reject incoming ringing call */
	REJECT = 'REJECT',
	/** Report fullscreen change */
	FULLSCREEN = 'FULLSCREEN'
}

export interface ZerotierResponse {
	/** True on success */
	success: boolean
	/** Status code in case of error */
	code?: number
	/** Status message in case of error */
	message?: string
	/** Data returned from zerotier */
	data?: unknown
}

export interface NotificationShowResponse {
	/** True on success */
	success: boolean
	/** User clicked on the notification */
	clicked: boolean
	/** User closed the notification */
	closed: boolean
}

/**
 * Beacon data
 */
export interface Beacon {
	/** Name of the beacon or id */
	name: string
	/** IBeacon UUID */
	uuid: string
	/** IBeacon major value */
	major: number
	/** IBeacon minor value */
	minor: number
	/** RSSI value */
	rssi: number
	/** Battery level in percent */
	battery: number | undefined
	/** Time when the beacon was last seen */
	lastSeen: Date
}

/**
 * Beacon event types
 */
export enum BeaconEvent {
	/** New beacon discovered */
	ENTERED = 'ENTERED',
	/** Beacon updated */
	UPDATED = 'UPDATED',
	/** Beacon left range */
	LEFT = 'LEFT',
	/** Beacon that is the closest has changed */
	CURRENT = 'CURRENT'
}

// #region Yealink headset integration

/** Options for starting the Yealink integration */
export interface YealinkStartOptions {
	/** Yealink App ID - defaults to the TelecomX App ID 'dk.telecomx.communicator' */
	appId?: string
	/** Name shown in the device's softphone chooser (displayed as 'PC Softphone <name>') - defaults to the current app name */
	softphoneName?: string
}

/** A contact used for caller ID on the device display and for phonebook entries */
export interface YealinkContact {
	/** Display name - the device draws the avatar initials from this */
	name: string
	/** Primary number (office) */
	number: string
	/** Optional - shown in the phonebook on richer UIs */
	company?: string
	/** Optional - additional number slot */
	mobile?: string
	/** Optional - additional number slot */
	other?: string
}

/** Identity of one attached Yealink device */
export interface YealinkDeviceInfo {
	/** SDK-assigned handle, stable for the duration of the attach - used as the deviceId parameter in all yealink* calls */
	deviceId: number
	/** USB vendor id - Yealink is 0x6993 */
	vendorId: number
	/** USB product id */
	productId: number
	/** Model name, e.g. 'WH66' */
	name: string
	/** Model variant */
	variant: string
	/** USB serial number */
	serialNumber: string
	/** True if this is a DECT dongle/base */
	isDongle: boolean
	/** Name of the paired handset when isDongle */
	dongleName: string
	/** True while the device is in firmware update mode */
	isInFirmwareUpdateMode: boolean
	/** How the device is connected */
	connectionType: 'USB' | 'BT' | 'Unknown'
}

/** Snapshot of one attached device: identity plus its supported features */
export interface YealinkDeviceSnapshot {
	/** Device identity */
	info: YealinkDeviceInfo
	/** Feature support map - key is a DeviceFeature enum name (e.g. 'BusyLight', 'Contact', 'RccMultiCall'), value is true when supported */
	features: Record<string, boolean>
}

/** State of the Yealink integration as reported by yealinkGetState() */
export interface YealinkState {
	/** True when the SDK is initialized and running */
	running: boolean
	/** Yealink SDK build version - empty string before the first successful start */
	sdkVersion: string
	/** All currently attached devices */
	devices: YealinkDeviceSnapshot[]
}

/** Serialized error information from a failed Yealink operation */
export interface YealinkErrorInfo {
	/** Human readable error message */
	message: string
	/** Numeric Yealink SDK return code, when the failure came from the SDK */
	code?: number
	/** Name of the SDK return code, e.g. 'Device_NotConnected' or 'Not_Supported' */
	codeName?: string
	/** The SDK function that failed, e.g. 'Yealink_AnswerCall' */
	sdkFunction?: string
	/** Device the operation was addressed to */
	deviceId?: number
	/** Call id the operation was addressed to */
	cid?: number
}

/** Result envelope returned by all yealink* operations */
export interface YealinkResult<T = null> {
	/** True on success */
	success: boolean
	/** The return value on success, for operations that return data */
	result?: T
	/** Error information on failure */
	error?: YealinkErrorInfo
}

/** Battery state as returned by yealinkGetBatteryStatus() */
export interface YealinkBatteryStatus {
	/** Battery level 0-100 */
	levelPercent: number
	/** True while charging */
	charging: boolean
	/** True when the battery is low */
	low: boolean
}

/** One device setting as pushed by the device */
export interface YealinkDeviceSetting {
	/** Setting identifier */
	guid: string
	/** Setting name */
	name: string
	/** Help text for the setting */
	helpText: string
	/** Current value */
	value: string | number
	/** True if changing the setting requires a device restart */
	requiresRestart: boolean
	/** True if the setting is protected */
	isProtected: boolean
}

/**
 * Name-to-numeric-value maps for all Yealink SDK enums, fetched once via yealinkGetEnums().
 * Numeric values mirror the Yealink SDK C headers exactly.
 */
export interface YealinkEnums {
	/** User call-control actions arriving on the 'callAction' event: Accept, Terminate, Hold, Resume, Reject, CallOut, EnterDial, Mute, Unmute, DTMF, Redial */
	CallAction: Record<string, number>
	/** Translated HID keys arriving on the 'hidInput' event: OffHook, Mute, Hold, Redial, Send, Reject, Key0-Key9, KeyStar, KeyPound, KeyVolumeUp, KeyVolumeDown, ... */
	HidInput: Record<string, number>
	/** Busylight states for yealinkSetBusylight(): Ring, Talking, Hold, Mute, Dialing, Online, Offline, Left, Busy, NotDisturb, OffWork, ... */
	BusylightStatus: Record<string, number>
	/** Feature bits for yealinkIsFeatureSupported(): FactoryReset, Reboot, SetDateTime, Battery, Contact, UploadRingtone, PlayRingtone, BusyLight, Settings, SetCurrentContact, RccMultiCall */
	DeviceFeature: Record<string, number>
	/** HID states: NotSupported, StandardHid, YealinkHid */
	HidState: Record<string, number>
	/** SDK return codes appearing in YealinkErrorInfo.code */
	YealinkReturnCode: Record<string, number>
	/** Multi-call status codes */
	YealinkCallReturn: Record<string, number>
	/** Firmware progress event types */
	FirmwareEventType: Record<string, number>
	/** Firmware progress event statuses */
	FirmwareEventStatus: Record<string, number>
}

/** Fires once after the SDK finishes its initial USB scan */
export interface YealinkScanCompleteEvent {
	type: 'scanComplete'
}

/** A device was attached */
export interface YealinkDeviceAttachedEvent {
	type: 'deviceAttached'
	/** Snapshot of the attached device */
	device: YealinkDeviceSnapshot
}

/** A device was removed */
export interface YealinkDeviceRemovedEvent {
	type: 'deviceRemoved'
	/** deviceId of the removed device */
	deviceId: number
}

/**
 * The user pressed a call-control button on the device.
 * This is a request, not a notification - the device does not change its own call state until the app applies the action via the corresponding yealink* method.
 */
export interface YealinkCallActionEvent {
	type: 'callAction'
	deviceId: number
	/** Call id - negative sentinel when the action is device-initiated and no app-side cid exists yet */
	cid: number
	/** CallAction enum value */
	action: number
	/** CallAction enum name, e.g. 'Accept', 'Terminate', 'DTMF', 'CallOut' */
	actionName: string
	/** DTMF only - the pressed key '0'-'9', '*' or '#' */
	digit?: string
	/** CallOut: full number typed on the device dialpad (digits only, a leading + is lost) - dial-entry DTMF: number typed so far - undefined for phonebook/redial dials */
	number?: string
}

/** Live keypad input during dial entry - fires on every keypress with the number typed so far */
export interface YealinkDialNumberChangedEvent {
	type: 'dialNumberChanged'
	deviceId: number
	/** Call id - negative sentinel until the app assigns a call id */
	cid: number
	/** The number typed so far (digits only) */
	number: string
}

/** Low-level translated HID key event - only needed for custom integrations */
export interface YealinkHidInputEvent {
	type: 'hidInput'
	deviceId: number
	/** HidInput enum value */
	translated: number
	/** HidInput enum name, e.g. 'OffHook', 'Key5' */
	translatedName: string
	/** True on press, false on release */
	pressed: boolean
}

/** Raw HID usage event - only needed for custom integrations */
export interface YealinkHidRawEvent {
	type: 'hidRaw'
	deviceId: number
	usagePage: number
	usage: number
	pressed: boolean
}

/** Battery state update */
export interface YealinkBatteryUpdateEvent {
	type: 'batteryUpdate'
	deviceId: number
	/** Battery level 0-100 */
	levelPercent: number
	/** True while charging */
	charging: boolean
	/** True when the battery is low */
	low: boolean
}

/** Busylight state changed on the device */
export interface YealinkBusylightEvent {
	type: 'busylight'
	deviceId: number
	/** BusylightStatus enum value */
	status: number
	/** BusylightStatus enum name, e.g. 'Talking' */
	statusName: string
}

/** The device gained or lost softphone focus */
export interface YealinkFocusChangeEvent {
	type: 'focusChange'
	deviceId: number
	inFocus: boolean
}

/** Firmware update progress */
export interface YealinkFirmwareProgressEvent {
	type: 'firmwareProgress'
	deviceId: number
	/** FirmwareEventType enum value */
	firmwareType: number
	/** FirmwareEventStatus enum value */
	status: number
	/** Progress 0-100 */
	percent: number
}

/** The device pushed a settings update */
export interface YealinkSettingsChangedEvent {
	type: 'settingsChanged'
	deviceId: number
	/** The settings as reported by the device */
	settings: YealinkDeviceSetting[]
}

/** All events emitted by the Yealink integration on the 'yealink' channel - discriminated on the 'type' field */
export type YealinkEvent =
	| YealinkScanCompleteEvent
	| YealinkDeviceAttachedEvent
	| YealinkDeviceRemovedEvent
	| YealinkCallActionEvent
	| YealinkDialNumberChangedEvent
	| YealinkHidInputEvent
	| YealinkHidRawEvent
	| YealinkBatteryUpdateEvent
	| YealinkBusylightEvent
	| YealinkFocusChangeEvent
	| YealinkFirmwareProgressEvent
	| YealinkSettingsChangedEvent

// #endregion
